import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import TicketCard from './components/TicketCard';
import { OrderProvider } from 'providers';
import {
  isValidProviderResult,
  DataStore,
  Logger,
  Environment,
  Colors,
  Theme,
  FontFamilies,
  FontSizes,
  MediaQueries,
  isValidEmail
} from 'common';
import {
  Text,
  Button,
} from 'common/components';
import { withCommon } from 'common/hocs';

const Container = styled.div`
  padding: 20px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const MaxWidthContainer = styled.div`
  width: 100%;
  max-width: 950px;
`;

const Title = styled.h1`
  font-size: 1.6em;
  margin: 20px 0 10px 0;
  color: ${Theme.Text.Color};
`;

const TicketList = styled.div`
  display: flex;
  flex-direction: column;
`;

const Divide = styled.div`
  border-bottom: 1px solid ${Theme.Divider.Color};
  margin: 15px 0 15px 0;
  width: 100%;
`;

const EmailContainer = styled.div`
  display: flex;
  flex-direction: column;

  @media only screen and ${MediaQueries.Md} {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

const ButtonContainer = styled.div`
  width: 100%;
  margin: 15px 0 0 0;

  @media only screen and ${MediaQueries.Md} {
    width: 125px;
    margin: 0 0 0 25px;
  }
`;

const TabBar = styled.div`
  display: flex;
  gap: 28px;
  width: 100%;
  margin: 4px 0 12px 0;
  border-bottom: 1px solid ${Theme.Divider.Color};
`;

const TabButton = styled.button`
  margin: 0;
  padding: 10px 0 12px 0;
  border: none;
  border-bottom: 2px solid ${props => props.$selected ? Theme.Text.Color : 'transparent'};
  margin-bottom: -1px;
  background: none;
  cursor: pointer;
  font-size: ${FontSizes.Small};
  font-family: ${props => props.$selected ? FontFamilies.Bold : FontFamilies.Regular};
  color: ${Theme.Text.Color};
  opacity: ${props => props.$selected ? 1 : 0.55};

  &:focus {
    outline: none;
  }
`;

const isPastOrder = (order) => {
  const rawEnd = order.is_multi_event ? order.multi_event_end_date : order.date;
  const end = new Date(rawEnd);

  if (!rawEnd || Number.isNaN(end.getTime())) {
    return false;
  }

  if (order.is_multi_event) {
    end.setHours(23, 59, 59, 999);
  }

  return end.getTime() < Date.now();
};

const ViewMyTicketsComponent = (props) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');

  // Authentication methods configuration
  const AUTH_CONFIG = {
    email: true,      // Enable/disable email authentication
    whatsapp: true,   // Enable/disable WhatsApp authentication
  };

  // Get available authentication methods
  const getAvailableMethods = () => {
    const methods = [];
    if (AUTH_CONFIG.email) methods.push('email');
    if (AUTH_CONFIG.whatsapp) methods.push('whatsapp');
    return methods;
  };

  // Get the contact type and display format
  const getContactInfo = () => {
    const contact = DataStore.get('LOGGED_IN_EMAIL');
    if (!contact) return { type: null, display: '', isEmail: false };
    
    const isEmail = isValidEmail(contact);
    return {
      type: isEmail ? 'email' : 'whatsapp',
      display: isEmail ? contact : `WhatsApp: ${contact}`,
      isEmail: isEmail
    };
  };

  // Get instruction text based on available methods and current contact
  const getInstructionText = () => {
    const availableMethods = getAvailableMethods();
    const contactInfo = getContactInfo();
    
    if (availableMethods.length === 0) {
      return 'Tickets displayed below were purchased using the contact below:';
    }
    
    if (availableMethods.length === 1) {
      if (availableMethods[0] === 'email') {
        return 'Tickets displayed below were purchased using the email address below:';
      } else {
        return 'Tickets displayed below were purchased using the WhatsApp number below:';
      }
    }
    
    // Both methods enabled
    if (contactInfo.isEmail) {
      return 'Tickets displayed below were purchased using the email address below:';
    } else {
      return 'Tickets displayed below were purchased using the WhatsApp number below:';
    }
  };

  // Get button text based on available methods and current contact
  const getButtonText = () => {
    const availableMethods = getAvailableMethods();
    
    if (availableMethods.length === 1) {
      if (availableMethods[0] === 'email') {
        return 'Change Email';
      } else {
        return 'Change WhatsApp';
      }
    }
    
    // Multiple methods or fallback
    return 'Change Contact';
  };

  const fetchTickets = async () => {
    try {
      const response = await OrderProvider.getOrdersByLoggedInUser(); // Fetch tickets from API

      if (!isValidProviderResult(response)) {
        throw Error(response.message || 'Couldn\'t load order data.');
      }

      const orders = response.data || [];
      setTickets(orders);

      const hasUpcoming = orders.some(order => !isPastOrder(order));
      const hasPast = orders.some(order => isPastOrder(order));
      if (!hasUpcoming && hasPast) {
        setActiveTab('past');
      }
    } catch (err) {
      Logger.error("ViewMyTickets", "load", err);
      props.common.showToast("Oops, something went wrong, please try again.", "error");
    } finally {
      setLoading(false);
      props.common.hideLoading();
    }
  };

  useEffect(() => {

    if (!DataStore.get('LOGGED_IN_EMAIL')) {
      props.common.navigate('/identity/otp-login');
      return;
    }

    fetchTickets()
      .finally(() => {
        // Check if update success flag is set by the EditCustomFields component
        if (props.common.location?.state?.updateSuccess) {

          props.common.showToast('Details updated successfully!', 'success');
          // Clear the update success flag to prevent infinite loop
          props.common.history.replace('/profile/view-my-tickets', {});
        }
      });
  }, []); // eslint-disable-line

  const onDownloadClick = async (token, id) => {

    props.common.showLoading();

    try {
      const response = await fetch(`${Environment.apiHost}payment/download-order`, {
          method: 'POST',
          headers: {
              'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token: token,
            addReceipt: false
          }),
      });

      if (!response.ok) {
        throw new Error('Failed to download PDF');
      }

      // Create a blob from the response
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      // Trigger the download
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `WT-${id}.pdf`); // Name of the downloaded file
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      props.common.showToast('Oh no, something went wrong, please try again.');
      Logger.error('ViewMyTickets', `onDownloadClick (${id})`, error)
    }
    finally {
      props.common.hideLoading()
    }
  };


  const contactInfo = getContactInfo();
  const upcomingTickets = tickets.filter(ticket => !isPastOrder(ticket));
  const pastTickets = tickets.filter(ticket => isPastOrder(ticket));
  const visibleTickets = activeTab === 'past' ? pastTickets : upcomingTickets;

  return (
    <Container>
      <MaxWidthContainer>
        <Text
          maxWidth={'1100px'}
          width={'100px'}
          display={'block'} 
          padding={'5px 0 0 0'} 
          fontSize={FontSizes.Small}
          color={Theme.Text.Color}
          onClick={() => props.common.navigate('/')}>
            ← Back
        </Text>
        <Title>My Tickets</Title>
        <Divide/>
        <EmailContainer>
          <div>
            <Text
              display={'block'}
              fontSize={FontSizes.Smaller}
              padding={'0 0 5px 0'}>
                {getInstructionText()}
            </Text>
            <Text fontSize={FontSizes.Small} fontFamily={FontFamilies.Bold}>
              {contactInfo.display}
            </Text>
          </div>
          <ButtonContainer>
            <Button
              backgroundColor={Colors.Transparent}
              border={Theme.EventIdentity.ButtonBorder}
              textColor={Theme.EventIdentity.ButtonTextColor}
              fontFamily={FontFamilies.Regular}
              fontSize={FontSizes.Smaller}
              margin={'0'}
              text={getButtonText()}
              width={'100%'}
              onClick={() => props.common.navigate('/identity/otp-login')}
            />
          </ButtonContainer>
        </EmailContainer>
        <Divide/>
        {loading && <Text>Loading tickets...</Text>}
        {!loading && tickets.length === 0 && (
          <Text fontSize={FontSizes.Small}>You don't have any tickets yet. Browse our events to find something exciting!</Text>
        )}
        {!loading && tickets.length > 0 && (
          <>
            <TabBar role="tablist" aria-label="Purchased tickets">
              <TabButton
                type="button"
                role="tab"
                aria-selected={activeTab === 'upcoming'}
                $selected={activeTab === 'upcoming'}
                onClick={() => setActiveTab('upcoming')}>
                Upcoming ({upcomingTickets.length})
              </TabButton>
              <TabButton
                type="button"
                role="tab"
                aria-selected={activeTab === 'past'}
                $selected={activeTab === 'past'}
                onClick={() => setActiveTab('past')}>
                Past ({pastTickets.length})
              </TabButton>
            </TabBar>
            <TicketList>
              {visibleTickets.length === 0 ? (
                <Text fontSize={FontSizes.Small}>
                  {activeTab === 'past'
                    ? 'You don\'t have any past tickets yet.'
                    : 'You don\'t have any upcoming tickets. Past purchases are on the Past tab.'}
                </Text>
              ) : (
                visibleTickets.map(ticket => (
                  <TicketCard
                    key={ticket.id}
                    ticket={ticket}
                    customFields={ticket.custom_fields || []}
                    onDownload={() => onDownloadClick(ticket.download_token, ticket.id)}
                    history={props.common.history}
                  />
                ))
              )}
            </TicketList>
          </>
        )}
      </MaxWidthContainer>
    </Container>
  );
};

export const ViewMyTickets = withCommon(ViewMyTicketsComponent);